import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { Popconfirm, Row, theme } from 'antd';
import { IGaps, IGapsInfo, ITextAreaBoxProps } from 'store/ducks/Gaps/types';
import { gapsOp } from 'store/ducks/Gaps';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import CustomTextarea from '../CustomTextarea';
import CustomIconButton from '../CustomIconButton';
import CustomCheckbox from '../CustomCheckbox';
import * as Styled from './TextAreaBoxGap.styled';

interface IPopoverState {
  [id: number | string]: boolean;
}

const { useToken } = theme;

export const TextAreaBoxGap: React.FC<ITextAreaBoxProps> = ({
  placeholder,
  data,
  withCheckbox = false,
  refId,
  onChange,
  onDelete
}) => {
  const { token } = useToken();
  const { scheme } = useSelector((state: RootState) => state.global);

  const { commentsData } = useSelector(
    (state: RootState) => state.complianceEst
  );

  const [dataObj, setDataObj] = useState<IGaps>({});
  const [checkboxDisabled, setCheckboxDisabled] = useState(
    data && !Helpers.isObjEmpty(data)
  );
  const [popoverState, setPopoverState] = useState<IPopoverState>({});

  useEffect(() => {
    let commentsState: IPopoverState = {};

    if (!Helpers.isObjEmpty(dataObj)) {
      Object.values(dataObj).forEach((item: IGapsInfo) => {
        if (item.gap_id) {
          commentsState[item.gap_id] = false;
        }
      });
    }

    setPopoverState(commentsState);
  }, [dataObj]);

  useEffect(() => {
    if (data && !Helpers.isObjEmpty(data)) {
      setDataObj(data);
      setCheckboxDisabled(true);
    }
  }, [data]);

  const handleAddEmptyGap = (id: string) => {
    const newEntry = { gap_desc: '', gap_bru_ref: refId };
    setDataObj((prevState: IGaps) => ({
      ...prevState,
      [id]: newEntry
    }));
    onChange({ [id]: newEntry });
  };

  const handlePlusClick = () => {
    const newId = uuidv4();

    handleAddEmptyGap(newId);
  };

  const handleDeleteClick = (key: number) => {
    setDataObj((prevState: IGaps) => {
      const copy = { ...prevState };
      delete copy[key];

      return copy;
    });

    if (data[key]) {
      gapsOp.deleteGap(key);
    }

    onDelete(key);
  };

  const handleGapChange = (value: string, key: number) => {
    setDataObj((prevState: IGaps) => ({
      ...prevState,
      [key]: {
        gap_id: prevState[key].gap_id,
        gap_desc: value,
        gap_bru_ref: refId
      }
    }));

    onChange({
      [key]: { ...dataObj[key], gap_desc: value, gap_bru_ref: refId }
    });
  };

  const handleCheckboxChange = () => {
    if (Helpers.isObjEmpty(dataObj)) {
      handlePlusClick();
    }
  };

  const hide = (id: number | string) => {
    if (!id) return;

    setPopoverState((prevState) => ({
      ...prevState,
      [id]: false
    }));
  };

  const handleOpenChange = (id: number | string) => {
    if (!id) return;

    setPopoverState((prevState) => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  useEffect(() => {
    if (dataObj && !Helpers.isObjEmpty(dataObj)) {
      setCheckboxDisabled(true);
    } else {
      setCheckboxDisabled(false);
    }
  }, [dataObj]);

  return (
    <Styled.StyledContainer withCheckbox={withCheckbox}>
      <Row>
        <Styled.CheckboxContainer>
          {withCheckbox && (
            <CustomCheckbox
              disabled={checkboxDisabled}
              checked={checkboxDisabled}
              onChange={handleCheckboxChange}
            />
          )}
        </Styled.CheckboxContainer>
        <Styled.TextAreaContainer withCheckbox={withCheckbox} scheme={scheme}>
          {dataObj &&
            Object.keys(dataObj).map((key: any, index: number) => (
              <Styled.TextAreaElementsContainer
                key={dataObj[key].gap_id || index}
              >
                <CustomTextarea
                  key={dataObj[key].gap_id}
                  placeholder={placeholder}
                  defaultValue={dataObj[key].gap_desc}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleGapChange(e.target.value, key)
                  }
                />
                <Styled.StyledButtonSection token={token}>
                  <Popconfirm
                    title='Are you sure you want to delete this gap?'
                    onConfirm={() => handleDeleteClick(key)}
                    okText='Yes'
                    cancelText='No'
                    placement='topRight'
                  >
                    <Styled.StyledCustomIconButton
                      token={token}
                      type='primary'
                      shape='circle'
                      icon={<DeleteOutlined />}
                      cursor='pointer'
                      size='small'
                    />
                  </Popconfirm>
                </Styled.StyledButtonSection>
              </Styled.TextAreaElementsContainer>
            ))}
          <Styled.ButtonContainer>
            <CustomIconButton
              type='primary'
              shape='circle'
              icon={<PlusOutlined />}
              size='middle'
              onClick={handlePlusClick}
            />
          </Styled.ButtonContainer>
        </Styled.TextAreaContainer>
      </Row>
      <Styled.FlexContainer />
    </Styled.StyledContainer>
  );
};
